// De dónde llegó el cliente a la página de un negocio (utm_*, click-ids o el
// sitio que lo mandó), para guardarlo en la orden (`order_attribution`).
//
// Mismas reglas que la atribución de las altas de Redy
// (lib/marketing/signupAttribution.ts): último toque con UTM o click-id en 30
// días, una visita directa no lo pisa y la navegación interna no es origen.
// Lo único distinto es la cookie (`redy_attr`, del host donde está la tienda:
// dominio propio o useredy.com) y que la tienda no usa códigos de referido.
//
// Con pago de paso (dominio propio → useredy.com/order/<slug>/pagar) no se
// pierde nada: el body de /api/online-orders/session sale del dominio, antes de
// la redirección, y lleva lo que diga esta cookie.
import {
  CLICK_ID_KEYS,
  UTM_KEYS,
  attributionFromVisit,
  decodeAttribution,
  encodeAttribution,
  isTagged,
  nextAttribution,
  readCookie,
  type SignupAttribution,
} from "@/lib/marketing/signupAttribution"

export const STORE_ATTR_COOKIE = "redy_attr"
export const STORE_ATTR_MAX_AGE_S = 30 * 24 * 60 * 60

/** Lo que viaja en el body de /session y del POST de la orden. Solo telemetría. */
export type OrderAttributionInput = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  fbclid?: string
  gclid?: string
  gbraid?: string
  wbraid?: string
  referrer_host?: string
  landing_path?: string
  touched_at?: string
}

type Visit = Parameters<typeof attributionFromVisit>[0]

/** El `?ref=` de referidos es de Redy, no de la tienda: fuera antes de decidir nada. */
function forStore(a: SignupAttribution | null): SignupAttribution | null {
  if (!a) return null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ref: _ref, ref_first_seen_at: _seen, ...rest } = a
  return isTagged(rest) || rest.referrer_host ? rest : null
}

/** Valor nuevo de la cookie tras esta visita, o null si no hay que escribir nada. */
export function nextStoreAttribution(cookieValue: string | null, visit: Visit): string | null {
  const next = nextAttribution(forStore(decodeAttribution(cookieValue)), forStore(attributionFromVisit(visit)))
  return next ? encodeAttribution(next) : null
}

/** Lo que dice la cookie, saneado, listo para el body. Null si no hay nada. */
export function attributionForOrder(cookieValue: string | null): OrderAttributionInput | null {
  const a = forStore(decodeAttribution(cookieValue))
  if (!a) return null
  const out: OrderAttributionInput = {}
  for (const k of UTM_KEYS) if (a[k]) out[k] = a[k]
  for (const k of CLICK_ID_KEYS) if (a[k]) out[k] = a[k]
  if (a.referrer_host) out.referrer_host = a.referrer_host
  if (a.landing_path) out.landing_path = a.landing_path
  out.touched_at = a.touched_at
  return out
}

/**
 * Campo listo para mezclar en el body de /session y del POST de la orden
 * (`{ ...attributionBodyField() }`). Vacío si no hay cookie, si no hay
 * `document` (SSR) o si el navegador bloquea cookies: medir nunca rompe el pago.
 */
export function attributionBodyField(): { attribution?: OrderAttributionInput } {
  try {
    if (typeof document === "undefined") return {}
    const a = attributionForOrder(readCookie(document.cookie, STORE_ATTR_COOKIE))
    return a ? { attribution: a } : {}
  } catch {
    return {}
  }
}

/**
 * Lo que hace <AttributionCapture /> al cargar la tienda, sin React ni DOM real
 * para poder probarlo en node: lee la cookie, decide y, si corresponde, la
 * reescribe (30 días, Lax, Secure en https). Devuelve si escribió.
 */
export function captureStoreAttribution(env: {
  doc: { cookie: string; referrer: string }
  loc: { search: string; pathname: string; hostname: string; protocol: string }
  now: Date
}): boolean {
  const next = nextStoreAttribution(readCookie(env.doc.cookie, STORE_ATTR_COOKIE), {
    search: env.loc.search,
    pathname: env.loc.pathname,
    referrer: env.doc.referrer,
    ownHost: env.loc.hostname,
    now: env.now,
  })
  if (!next) return false
  const secure = env.loc.protocol === "https:" ? "; Secure" : ""
  env.doc.cookie = `${STORE_ATTR_COOKIE}=${next}; Path=/; Max-Age=${STORE_ATTR_MAX_AGE_S}; SameSite=Lax${secure}`
  return true
}
